import { apiClient } from './apiClient';

// Serviço de upload e processamento de CSV
export const csvService = {
  // Upload de arquivo CSV único
  async uploadCsv(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post('/api/v1/csv/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutos para upload
    });
    
    return response.data;
  },

  // Upload de múltiplos arquivos CSV
  async uploadMultipleCsv(files) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    const response = await apiClient.post('/api/v1/csv/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000, // 3 minutos para múltiplos uploads
    });
    
    return response.data;
  },

  // Obter status do processamento
  async getProcessingStatus(taskId) {
    const response = await apiClient.get(`/api/v1/csv/status/${taskId}`);
    return response.data;
  },

  // Obter resultado final do processamento
  async getProcessingResult(taskId) {
    const response = await apiClient.get(`/api/v1/csv/result/${taskId}`);
    return response.data;
  },

  // Listar tarefas ativas
  async getActiveTasks() {
    const response = await apiClient.get('/api/v1/csv/active-tasks');
    return response.data;
  },

  // Limpar tarefas antigas (admin only)
  async cleanupOldTasks(maxAgeHours = 24) {
    const response = await apiClient.post('/api/v1/csv/cleanup-old-tasks', null, {
      params: { max_age_hours: maxAgeHours }
    });
    return response.data;
  },

  // Monitorar progresso do processamento
  async monitorProcessing(taskId, onProgress, onComplete, onError) {
    const pollInterval = 2000; // 2 segundos
    let isCompleted = false;

    const poll = async () => {
      try {
        const status = await this.getProcessingStatus(taskId);
        
        if (onProgress) {
          onProgress(status);
        }

        // Verificar se processamento terminou
        if (status.status === 'completed' || status.status === 'failed') {
          isCompleted = true;
          
          if (status.status === 'completed' && onComplete) {
            // Obter resultado completo
            try {
              const result = await this.getProcessingResult(taskId);
              onComplete(result);
            } catch (error) {
              // Se não conseguir obter resultado, usar status
              onComplete(status);
            }
          } else if (status.status === 'failed' && onError) {
            onError(new Error(status.message || 'Processamento falhou'));
          }
        } else {
          // Continuar monitorando
          setTimeout(poll, pollInterval);
        }
      } catch (error) {
        isCompleted = true;
        if (onError) {
          onError(error);
        }
      }
    };

    // Iniciar monitoramento
    poll();

    // Retornar função para cancelar monitoramento
    return () => {
      isCompleted = true;
    };
  },

  // Validar arquivo CSV antes do upload
  validateCsvFile(file) {
    const errors = [];
    
    // Verificar tipo de arquivo
    if (!file.type.includes('csv') && !file.name.toLowerCase().endsWith('.csv')) {
      errors.push('Arquivo deve ser do tipo CSV');
    }

    // Verificar tamanho (500MB máximo)
    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push(`Arquivo muito grande. Máximo permitido: ${(maxSize / 1024 / 1024).toFixed(1)}MB`);
    }
    
    // Verificar se arquivo não está vazio
    if (file.size === 0) {
      errors.push('Arquivo está vazio');
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  },

  // Formatar tamanho de arquivo para exibição
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Calcular tempo estimado de processamento
  estimateProcessingTime(fileSize, estimatedRows) {
    // Estimativa baseada em ~1000 registros por segundo
    const rowsPerSecond = 1000;
    const baseTime = 10; // tempo mínimo base
    
    let estimatedTime = baseTime;
    if (estimatedRows > 0) {
      estimatedTime = Math.max(baseTime, Math.ceil(estimatedRows / rowsPerSecond));
    } else {
      // Estimar baseado no tamanho do arquivo
      const estimatedRowsBySize = Math.ceil(fileSize / 200); // ~200 bytes por linha
      estimatedTime = Math.max(baseTime, Math.ceil(estimatedRowsBySize / rowsPerSecond));
    }
    
    return estimatedTime;
  },

  // Formatar tempo em formato legível
  formatTime(seconds) {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  },

  // Formatar status para exibição
  formatStatus(status) {
    const statusMap = {
      'pending': 'Pendente',
      'processing': 'Processando',
      'completed': 'Concluído',
      'failed': 'Falhou'
    };
    
    return statusMap[status] || status;
  },

  // Obter cor do status para UI
  getStatusColor(status) {
    const colorMap = {
      'pending': '#FFA726',      // Laranja
      'processing': '#42A5F5',   // Azul
      'completed': '#66BB6A',    // Verde
      'failed': '#EF5350'        // Vermelho
    };
    
    return colorMap[status] || '#757575'; // Cinza padrão
  }
};

export default csvService;